<template>
  <div>
    <!-- Общие фильтры: пользователь, поиск, период -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <div class="d-flex justify-content-between align-items-center w-100">
          <h5 class="mb-0">Оплаты генератора</h5>
          <b-button
              v-if="hasActiveFilters"
              variant="outline-danger"
              size="sm"
              @click="resetFilters"
          >
            <feather-icon icon="XIcon" class="mr-25" />
            Сбросить
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Пользователь</label>
            <b-input-group>
              <b-form-input
                  v-model="searchQuery"
                  placeholder="Имя или email..."
                  @keyup.enter="applyFilters()"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="applyFilters()">
                  Применить
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">С</label>
            <b-form-input
                v-model="filters.date_from"
                type="date"
                @change="applyFilters()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">По</label>
            <b-form-input
                v-model="filters.date_to"
                type="date"
                @change="applyFilters()"
            />
          </b-col>
          <b-col v-if="activeTabKey === 'payments'" cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Статус</label>
            <v-select
                v-model="filters.status"
                :options="paymentStatusOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="applyFilters()"
            />
          </b-col>
          <b-col v-else cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Тип</label>
            <v-select
                v-model="filters.kind"
                :options="transactionKindOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="applyFilters()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1 d-flex align-items-end">
            <b-badge
                v-if="filters.user_id"
                variant="light-primary"
                class="user-filter-chip"
                title="Сбросить фильтр по пользователю"
                @click="clearUserFilter"
            >
              Пользователь #{{ filters.user_id }}
              <feather-icon icon="XIcon" class="ml-25" />
            </b-badge>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-tabs v-model="activeTabIndex" pills class="mb-0">
      <!-- Платежи -->
      <b-tab title="Платежи">
        <b-row v-if="paymentsSummary">
          <b-col cols="12" md="4">
            <statistic-card-horizontal
                icon="CheckCircleIcon"
                color="success"
                :statistic="paymentsSummary.succeeded_count"
                statistic-title="Успешных платежей"
            />
          </b-col>
          <b-col cols="12" md="4">
            <statistic-card-horizontal
                icon="DollarSignIcon"
                color="primary"
                :statistic="formatMoney(paymentsSummary.succeeded_sum)"
                statistic-title="Сумма успешных"
            />
          </b-col>
          <b-col cols="12" md="4">
            <statistic-card-horizontal
                icon="CreditCardIcon"
                color="secondary"
                :statistic="paymentsSummary.total_count"
                statistic-title="Всего платежей"
            />
          </b-col>
        </b-row>

        <b-card no-body>
          <b-table
              :items="paymentsList"
              :fields="paymentColumns"
              responsive
              primary-key="id"
              show-empty
              empty-text="Ничего не найдено"
              class="position-relative"
              :sort-by.sync="paymentsSortBy"
              :sort-desc.sync="paymentsSortDesc"
              no-local-sorting
              :busy="paymentsLoading"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle mr-50" />
                <strong>Загрузка...</strong>
              </div>
            </template>

            <template #cell(created_at)="data">
              <span class="text-nowrap">{{ formatDate(data.item.created_at) }}</span>
            </template>

            <template #cell(user)="data">
              <payment-user-cell :user="data.item.user" @filter="setUserFilter" />
            </template>

            <template #cell(amount)="data">
              <span class="font-weight-bold text-nowrap">{{ formatMoney(data.item.amount) }}</span>
            </template>

            <template #cell(status)="data">
              <b-badge :variant="paymentStatusVariant(data.item.status)">
                {{ paymentStatusLabel(data.item.status) }}
              </b-badge>
            </template>

            <template #cell(provider_payment_id)="data">
              <code v-if="data.item.provider_payment_id" class="font-small-2">{{ data.item.provider_payment_id }}</code>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(error_message)="data">
              <small v-if="data.item.error_message" class="text-danger">{{ data.item.error_message }}</small>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>

          <b-card-body class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
              <v-select
                  v-model="paymentsPerPage"
                  :options="perPageOptions"
                  :clearable="false"
                  style="min-width: 80px"
                  @input="applyFilters()"
              />
            </div>
            <b-pagination
                v-if="paymentsTotal > 0"
                v-model="paymentsPage"
                :total-rows="paymentsTotal"
                :per-page="paymentsPerPage"
                first-number
                last-number
                class="mb-0"
                prev-class="prev-item"
                next-class="next-item"
            />
          </b-card-body>
        </b-card>
      </b-tab>

      <!-- Движения баланса -->
      <b-tab title="Движения баланса">
        <b-card no-body>
          <b-table
              :items="transactionsList"
              :fields="transactionColumns"
              responsive
              primary-key="id"
              show-empty
              empty-text="Ничего не найдено"
              class="position-relative"
              :busy="transactionsLoading"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle mr-50" />
                <strong>Загрузка...</strong>
              </div>
            </template>

            <template #cell(created_at)="data">
              <span class="text-nowrap">{{ formatDate(data.item.created_at) }}</span>
            </template>

            <template #cell(user)="data">
              <payment-user-cell :user="data.item.user" @filter="setUserFilter" />
            </template>

            <template #cell(amount)="data">
              <span
                  class="font-weight-bold text-nowrap"
                  :class="data.item.amount >= 0 ? 'text-success' : 'text-danger'"
              >
                {{ formatSignedMoney(data.item.amount) }}
              </span>
            </template>

            <template #cell(kind)="data">
              <b-badge :variant="transactionKindVariant(data.item.kind)">
                {{ transactionKindLabel(data.item.kind) }}
              </b-badge>
            </template>

            <template #cell(description)="data">
              <span v-if="data.item.description">{{ data.item.description }}</span>
              <small v-else-if="data.item.identifier" class="text-muted">{{ data.item.identifier }}</small>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(pack_id)="data">
              <b-link
                  v-if="data.item.pack_id"
                  :to="{ name: 'generator-pack-detail', params: { id: data.item.pack_id } }"
              >
                #{{ data.item.pack_id }}
              </b-link>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>

          <b-card-body class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
              <v-select
                  v-model="transactionsPerPage"
                  :options="perPageOptions"
                  :clearable="false"
                  style="min-width: 80px"
                  @input="applyFilters()"
              />
            </div>
            <b-pagination
                v-if="transactionsTotal > 0"
                v-model="transactionsPage"
                :total-rows="transactionsTotal"
                :per-page="transactionsPerPage"
                first-number
                last-number
                class="mb-0"
                prev-class="prev-item"
                next-class="next-item"
            />
          </b-card-body>
        </b-card>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BPagination,
  BRow,
  BSpinner,
  BTab,
  BTable,
  BTabs,
} from 'bootstrap-vue'
import store from '@/store'
import { computed, onUnmounted, ref, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import generatorStoreModule from '@/views/generator/generatorStoreModule'
import { PAYMENT_STATUSES, TRANSACTION_KINDS } from '@/views/generator/packDictionaries'

const TAB_KEYS = ['payments', 'transactions']
const PER_PAGE_OPTIONS = [20, 30, 50, 100]

// Ячейка «Пользователь»: имя, email и баланс; клик ставит фильтр по пользователю
const PaymentUserCell = {
  name: 'PaymentUserCell',
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  render(createElement) {
    if (!this.user) return createElement('span', { class: 'text-muted' }, '—')

    return createElement('div', [
      createElement(BLink, {
        class: 'font-weight-bold',
        attrs: { title: 'Показать только этого пользователя' },
        on: { click: () => this.$emit('filter', this.user.id) },
      }, this.user.name || `#${this.user.id}`),
      createElement('div', [createElement('small', { class: 'text-muted' }, this.user.email || '')]),
      this.user.balance !== undefined && this.user.balance !== null
        ? createElement('div', [createElement('small', { class: 'text-muted' }, `Баланс: ${this.user.balance} ₽`)])
        : null,
    ])
  },
}

function getDefaultFilters() {
  return {
    user_id: null,
    date_from: null,
    date_to: null,
    status: null,
    kind: null,
  }
}

const FILTER_KEYS = Object.keys(getDefaultFilters())
// Фильтры, которые относятся только к одной вкладке
const TAB_ONLY_FILTERS = { payments: ['status'], transactions: ['kind'] }

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
    vSelect,
    StatisticCardHorizontal,
    BPagination,
    PaymentUserCell,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-generator'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, generatorStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // --- State ---
    const activeTabIndex = ref(0)
    const searchQuery = ref(null)
    const filters = ref(getDefaultFilters())

    const paymentsList = ref([])
    const paymentsTotal = ref(null)
    const paymentsSummary = ref(null)
    const paymentsPage = ref(1)
    const paymentsPerPage = ref(20)
    const paymentsSortBy = ref('id')
    const paymentsSortDesc = ref(true)
    const paymentsLoading = ref(false)

    const transactionsList = ref([])
    const transactionsTotal = ref(null)
    const transactionsPage = ref(1)
    const transactionsPerPage = ref(20)
    const transactionsLoading = ref(false)

    const activeTabKey = computed(() => TAB_KEYS[activeTabIndex.value] || TAB_KEYS[0])

    // --- Read URL query on init ---
    const route = root.$route
    const initialTabIndex = TAB_KEYS.indexOf(route.query.tab)
    if (initialTabIndex !== -1) activeTabIndex.value = initialTabIndex
    if (route.query.q) searchQuery.value = route.query.q
    FILTER_KEYS.forEach(key => {
      if (route.query[key] !== undefined && route.query[key] !== '') {
        filters.value[key] = route.query[key]
      }
    })
    const initialPage = parseInt(route.query.page, 10) || 1
    const initialPerPage = parseInt(route.query.per_page, 10) || 20
    if (activeTabKey.value === 'payments') {
      paymentsPage.value = initialPage
      paymentsPerPage.value = initialPerPage
      if (route.query.sort_by) paymentsSortBy.value = route.query.sort_by
      if (route.query.sort_desc !== undefined) paymentsSortDesc.value = route.query.sort_desc === '1' || route.query.sort_desc === 'true'
    } else {
      transactionsPage.value = initialPage
      transactionsPerPage.value = initialPerPage
    }

    const isFilterRelevant = key => {
      const tabOnly = Object.keys(TAB_ONLY_FILTERS).find(tab => TAB_ONLY_FILTERS[tab].includes(key))
      return !tabOnly || tabOnly === activeTabKey.value
    }

    const isFilled = value => value !== null && value !== '' && value !== undefined

    const hasActiveFilters = computed(() => isFilled(searchQuery.value)
      || FILTER_KEYS.some(key => isFilterRelevant(key) && isFilled(filters.value[key])))

    // --- Columns (ключи сортируемых колонок = поля sort_by на бэке) ---
    const paymentColumns = [
      { key: 'id', label: 'ID', sortable: true },
      { key: 'created_at', label: 'Дата', sortable: true },
      { key: 'user', label: 'Пользователь', sortable: false },
      { key: 'amount', label: 'Сумма', sortable: true },
      { key: 'status', label: 'Статус', sortable: true },
      { key: 'provider_payment_id', label: 'ID ЮKassa', sortable: false },
      { key: 'error_message', label: 'Ошибка', sortable: false },
    ]

    const transactionColumns = [
      { key: 'id', label: 'ID' },
      { key: 'created_at', label: 'Дата' },
      { key: 'user', label: 'Пользователь' },
      { key: 'amount', label: 'Сумма' },
      { key: 'kind', label: 'Тип' },
      { key: 'description', label: 'Описание' },
      { key: 'pack_id', label: 'Пак' },
    ]

    const paymentStatusOptions = Object.keys(PAYMENT_STATUSES)
      .map(value => ({ value, label: PAYMENT_STATUSES[value].label }))
    const transactionKindOptions = Object.keys(TRANSACTION_KINDS)
      .map(value => ({ value, label: TRANSACTION_KINDS[value].label }))

    // --- API ---
    const buildQueryParams = () => {
      const isPayments = activeTabKey.value === 'payments'
      const params = {
        tab: activeTabKey.value,
        q: searchQuery.value || undefined,
        page: isPayments ? paymentsPage.value : transactionsPage.value,
        per_page: isPayments ? paymentsPerPage.value : transactionsPerPage.value,
      }
      if (isPayments) {
        params.sort_by = paymentsSortBy.value
        params.sort_desc = paymentsSortDesc.value
      }
      FILTER_KEYS.forEach(key => {
        if (isFilterRelevant(key) && isFilled(filters.value[key])) {
          params[key] = filters.value[key]
        }
      })
      return params
    }

    const syncUrlQuery = params => {
      const query = {}
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined) {
          query[key] = String(params[key])
        }
      })
      const currentQuery = root.$route.query
      const isSame = Object.keys(query).length === Object.keys(currentQuery).length
        && Object.keys(query).every(key => currentQuery[key] === query[key])
      if (!isSame) {
        root.$router.replace({ query }).catch(() => {})
      }
    }

    const fetchPayments = params => {
      paymentsLoading.value = true
      store.dispatch('app-generator/fetchPayments', params)
        .then(response => {
          paymentsList.value = response.data.data
          paymentsTotal.value = response.data.meta ? response.data.meta.total : null
          paymentsSummary.value = response.data.summary || null
        })
        .finally(() => {
          paymentsLoading.value = false
        })
    }

    const fetchTransactions = params => {
      transactionsLoading.value = true
      store.dispatch('app-generator/fetchTransactions', params)
        .then(response => {
          transactionsList.value = response.data.data
          transactionsTotal.value = response.data.meta ? response.data.meta.total : null
        })
        .finally(() => {
          transactionsLoading.value = false
        })
    }

    const fetchActiveTab = () => {
      const params = buildQueryParams()
      syncUrlQuery(params)
      // tab — служебный параметр страницы, на бэкенд не отправляем
      const { tab, ...requestParams } = params
      if (tab === 'payments') fetchPayments(requestParams)
      else fetchTransactions(requestParams)
    }

    // Смена страницы через сеттер ref вызывает watcher; если страница уже 1 — грузим вручную
    const applyFilters = () => {
      const pageRef = activeTabKey.value === 'payments' ? paymentsPage : transactionsPage
      if (pageRef.value !== 1) {
        pageRef.value = 1
        return
      }
      fetchActiveTab()
    }

    const resetFilters = () => {
      filters.value = getDefaultFilters()
      searchQuery.value = null
      applyFilters()
    }

    const setUserFilter = userId => {
      filters.value.user_id = String(userId)
      applyFilters()
    }

    const clearUserFilter = () => {
      filters.value.user_id = null
      applyFilters()
    }

    // --- Formatting ---
    const formatDate = value => (value ? new Date(value).toLocaleString('ru-RU') : '—')
    const formatNumber = value => Number(value || 0).toLocaleString('ru-RU')
    const formatMoney = value => `${formatNumber(value)} ₽`
    const formatSignedMoney = value => {
      const amount = Number(value || 0)
      // «−» (минус), а не дефис — так ровнее в таблице
      return amount >= 0 ? `+${formatNumber(amount)} ₽` : `−${formatNumber(Math.abs(amount))} ₽`
    }

    const paymentStatusLabel = status => (PAYMENT_STATUSES[status] ? PAYMENT_STATUSES[status].label : status)
    const paymentStatusVariant = status => (PAYMENT_STATUSES[status] ? PAYMENT_STATUSES[status].variant : 'light-secondary')
    const transactionKindLabel = kind => (TRANSACTION_KINDS[kind] ? TRANSACTION_KINDS[kind].label : kind)
    const transactionKindVariant = kind => (TRANSACTION_KINDS[kind] ? TRANSACTION_KINDS[kind].variant : 'light-secondary')

    // --- Watchers ---
    watch([paymentsPage, paymentsSortBy, paymentsSortDesc], () => {
      if (activeTabKey.value === 'payments') fetchActiveTab()
    })

    watch(transactionsPage, () => {
      if (activeTabKey.value === 'transactions') fetchActiveTab()
    })

    watch(activeTabIndex, () => {
      fetchActiveTab()
    })

    // Initial fetch
    fetchActiveTab()

    return {
      activeTabIndex,
      activeTabKey,
      searchQuery,
      filters,
      hasActiveFilters,
      perPageOptions: PER_PAGE_OPTIONS,

      paymentsList,
      paymentsTotal,
      paymentsSummary,
      paymentsPage,
      paymentsPerPage,
      paymentsSortBy,
      paymentsSortDesc,
      paymentsLoading,
      paymentColumns,
      paymentStatusOptions,

      transactionsList,
      transactionsTotal,
      transactionsPage,
      transactionsPerPage,
      transactionsLoading,
      transactionColumns,
      transactionKindOptions,

      applyFilters,
      resetFilters,
      setUserFilter,
      clearUserFilter,
      formatDate,
      formatMoney,
      formatSignedMoney,
      paymentStatusLabel,
      paymentStatusVariant,
      transactionKindLabel,
      transactionKindVariant,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.user-filter-chip {
  cursor: pointer;
  padding: 0.5rem 0.75rem;
}
</style>
